import {
  buildCalendlyEmbedUrl,
  ensureCalendlyScript,
  openCalendlyPopup,
  slotToLocalDateKey,
  waitForCalendlyScheduled,
} from './calendlyEmbed'
import supabase from '../supabaseClient'

export type CalendlyAudience = 'community' | 'academy' | 'venue'

export type AvailabilitySlot = {
  start_time: string
  invitees_remaining: number
}

export type CalendlyBookingResult = {
  status: string
  cancel_url: string | null
  reschedule_url: string | null
  invitee_uri: string | null
  event_uri: string | null
  start_time: string
  timezone: string
}

export type DemoLeadPayload = {
  name: string
  email: string
  organization?: string
  audience: CalendlyAudience
  phone?: string
  message?: string
}

function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

function toIsoUtc(date: Date) {
  return date.toISOString()
}

function startOfUtcDay(date: Date) {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  )
}

function addUtcDays(date: Date, days: number) {
  const next = new Date(date)
  next.setUTCDate(next.getUTCDate() + days)
  return next
}

/** Earliest bookable slot: same day, at least 1 hour from now. */
export const MIN_BOOKING_LEAD_MS = 60 * 60 * 1000

export function earliestBookableTime(now = Date.now()) {
  return new Date(now + MIN_BOOKING_LEAD_MS)
}

/** Calendly requires start_time in the future; first chunk starts at now + 1 hour for same-day booking. */
function availabilityChunkStart(baseDay: Date, offsetDays: number) {
  const chunkStart = addUtcDays(startOfUtcDay(baseDay), offsetDays)
  if (offsetDays === 0) {
    const minStart = earliestBookableTime()
    return chunkStart.getTime() < minStart.getTime() ? minStart : chunkStart
  }
  return chunkStart
}

function isSlotBookable(startTime: string, now = Date.now()) {
  return Date.parse(startTime) >= now + MIN_BOOKING_LEAD_MS
}

function calendlyFunctionUrl(fn: 'calendly-availability' | 'calendly-book') {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  if (!supabaseUrl) {
    throw new Error('Supabase is not configured (VITE_SUPABASE_URL).')
  }
  return `${supabaseUrl}/functions/v1/${fn}`
}

const AVAILABILITY_CACHE_MS = 2 * 60_000
const availabilityCache = new Map<
  string,
  { at: number; slots: AvailabilitySlot[] }
>()

function availabilityCacheKey(days: number, audience?: CalendlyAudience) {
  const dayBucket = new Date().toISOString().slice(0, 10)
  return `${audience ?? 'default'}:${days}:${dayBucket}`
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isCalendlyRateLimited(message: string) {
  return /\b429\b/.test(message) || /rate limit/i.test(message)
}

async function invokeCalendly<T>(
  fn: 'calendly-availability' | 'calendly-book',
  body: Record<string, unknown>,
): Promise<T> {
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_KEY
  if (!supabaseAnonKey) {
    throw new Error('Supabase is not configured (VITE_SUPABASE_KEY).')
  }

  const {
    data: { session },
  } = await supabase.auth.getSession()

  const maxAttempts = fn === 'calendly-book' ? 2 : 3

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const response = await fetch(calendlyFunctionUrl(fn), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${session?.access_token || supabaseAnonKey}`,
      },
      body: JSON.stringify(body),
    })

    let payload: ({ error?: string } & T) | null = null
    try {
      payload = (await response.json()) as { error?: string } & T
    } catch {
      payload = null
    }

    const message =
      payload?.error ||
      (typeof (payload as { message?: string } | null)?.message === 'string'
        ? (payload as { message: string }).message
        : null) ||
      `${fn} failed (${response.status})`

    const rateLimited =
      response.status === 429 || isCalendlyRateLimited(message)

    if (rateLimited && attempt < maxAttempts - 1) {
      const retryAfterHeader = response.headers.get('Retry-After')
      const retryMs = retryAfterHeader
        ? Math.max(Number(retryAfterHeader) * 1000, 1000)
        : 1500 * 2 ** attempt
      await sleep(retryMs)
      continue
    }

    if (!response.ok) {
      throw new Error(message)
    }

    if (payload?.error) {
      throw new Error(payload.error)
    }

    return (payload ?? {}) as T
  }

  throw new Error(`${fn} failed after retries`)
}

/** Fetch open slots across a window (max 7 days per Calendly request). */
export async function fetchAvailabilityWindow(
  start: Date,
  end: Date,
  audience?: CalendlyAudience,
) {
  const response = await invokeCalendly<{
    slots: AvailabilitySlot[]
    timezone: string
  }>('calendly-availability', {
    start_time: toIsoUtc(start),
    end_time: toIsoUtc(end),
    timezone: getTimezone(),
    audience,
  })

  return response.slots || []
}

/** Load the next N days of availability (one Calendly request per 7-day chunk). */
export async function fetchUpcomingAvailability(
  days = 7,
  audience?: CalendlyAudience,
) {
  const cacheKey = availabilityCacheKey(days, audience)
  const cached = availabilityCache.get(cacheKey)
  if (cached && Date.now() - cached.at < AVAILABILITY_CACHE_MS) {
    return cached.slots
  }

  const today = new Date()
  const merged: AvailabilitySlot[] = []

  for (let offset = 0; offset < days; offset += 7) {
    const chunkStart = availabilityChunkStart(today, offset)
    const chunkEnd = addUtcDays(startOfUtcDay(today), Math.min(offset + 7, days))
    if (chunkEnd.getTime() <= chunkStart.getTime()) continue
    const chunk = await fetchAvailabilityWindow(chunkStart, chunkEnd, audience)
    merged.push(...chunk)
  }

  const seen = new Set<string>()
  const slots = merged.filter((slot) => {
    if (!isSlotBookable(slot.start_time)) return false
    if (seen.has(slot.start_time)) return false
    seen.add(slot.start_time)
    return true
  })

  availabilityCache.set(cacheKey, { at: Date.now(), slots })
  return slots
}

export function formatCalendlyLoadError(message: string) {
  if (isCalendlyRateLimited(message)) {
    return "Calendly is taking a short breather. Wait about 30 seconds, then refresh — we'll have times again."
  }
  return message
}

export function isCalendlySchedulingApiBlocked(message: string) {
  return /paid calendly plans|scheduling api/i.test(message)
}

export function formatCalendlyBookError(message: string) {
  if (isCalendlyRateLimited(message)) {
    return "Calendly is taking a short breather. Wait about 30 seconds, then try again."
  }
  if (/supplied parameters are invalid/i.test(message)) {
    return "We couldn't book that time. Want to try another slot?"
  }
  if (/already been scheduled|already scheduled/i.test(message)) {
    return "Looks like you already have something at this time. Pick another slot, or check your email for the invite."
  }
  if (/location kind is not configured/i.test(message)) {
    return "We couldn't start that video call. Try again, or email us at support@statbox.co — we'll help."
  }
  return message
}

const CALENDLY_PHONE_PLACEHOLDER = '+12025551234'

function normalizeCalendlyPhone(raw?: string): string {
  const trimmed = raw?.trim() ?? ''
  const digits = trimmed.replace(/\D/g, '')
  if (digits.length >= 6) {
    return trimmed.startsWith('+') ? trimmed : `+${digits}`
  }
  return CALENDLY_PHONE_PLACEHOLDER
}

export function normalizeCalendlyName(name: string) {
  return name.trim().replace(/[,;]+/g, ' ').replace(/\s+/g, ' ')
}

export function hasFirstAndLastName(name: string) {
  const parts = normalizeCalendlyName(name).split(/\s+/).filter(Boolean)
  return parts.length >= 2 && parts.every((part) => part.length >= 1)
}

export function validateCalendlyLead(lead: DemoLeadPayload): string | null {
  const name = normalizeCalendlyName(lead.name ?? '')

  if (name.length < 2) {
    return 'Please add your name.'
  }
  if (!hasFirstAndLastName(name)) {
    return 'Please enter your first and last name (Calendly requires both).'
  }
  return null
}

/** Calendly still expects these fields — fill quietly when the user skips optional inputs. */
export function prepareCalendlyLeadForApi(lead: DemoLeadPayload): {
  organization: string
  phone: string
  message: string
} {
  const organization = lead.organization?.trim() || 'Not provided'
  const phone = normalizeCalendlyPhone(lead.phone)
  const message =
    lead.message?.trim() ||
    `Statbox demo booking (${lead.audience}) — no extra details provided`

  return { organization, phone, message }
}

/** Edge function missing, misconfigured, or Calendly API unavailable — use embed fallback. */
export function isCalendlyEdgeUnavailable(message: string) {
  const m = message.toLowerCase()
  return (
    isCalendlySchedulingApiBlocked(message) ||
    m.includes('non-2xx') ||
    m.includes('edge function') ||
    m.includes('calendly_api_token') ||
    m.includes('event_type_uri') ||
    m.includes('failed to fetch') ||
    m.includes('load failed') ||
    m.includes('networkerror') ||
    m.includes('network request failed') ||
    /calendly-(availability|book) failed \((404|500|502|503)\)/.test(m) ||
    /function.*not found/.test(m)
  )
}

/** Free-plan fallback: open Calendly popup with day + contact pre-filled. */
export async function bookCalendlyDemoViaPopup(
  lead: DemoLeadPayload,
  startTime: string,
): Promise<CalendlyBookingResult> {
  const timezone = getTimezone()
  await ensureCalendlyScript()

  const url = buildCalendlyEmbedUrl({
    audience: lead.audience,
    name: lead.name,
    email: lead.email,
    date: slotToLocalDateKey(startTime, timezone),
    utmMedium: 'book_demo_fallback',
  })

  openCalendlyPopup(url)
  await waitForCalendlyScheduled()

  return {
    status: 'active',
    cancel_url: null,
    reschedule_url: null,
    invitee_uri: null,
    event_uri: null,
    start_time: startTime,
    timezone,
  }
}

export async function bookCalendlyDemo(
  lead: DemoLeadPayload,
  startTime: string,
): Promise<CalendlyBookingResult> {
  const timezone = getTimezone()
  const validationError = validateCalendlyLead(lead)
  if (validationError) {
    throw new Error(validationError)
  }

  const { organization, phone, message } = prepareCalendlyLeadForApi(lead)

  try {
    const response = await invokeCalendly<{ booking: CalendlyBookingResult }>(
      'calendly-book',
      {
        name: normalizeCalendlyName(lead.name),
        email: lead.email.trim(),
        organization,
        audience: lead.audience,
        phone,
        message,
        start_time: startTime,
        timezone,
      },
    )

    return response.booking
  } catch (err) {
    const errMessage = err instanceof Error ? err.message : 'Booking failed.'
    throw new Error(formatCalendlyBookError(errMessage))
  } finally {
    availabilityCache.clear()
  }
}

export function formatSlotLabel(iso: string, timezone = getTimezone()) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso))
}

export function formatSlotTime(iso: string, timezone = getTimezone()) {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso))
}

export function groupSlotsByLocalDay(
  slots: AvailabilitySlot[],
  timezone = getTimezone(),
) {
  const groups = new Map<string, AvailabilitySlot[]>()

  for (const slot of slots) {
    const dayKey = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone,
    }).format(new Date(slot.start_time))

    const list = groups.get(dayKey) || []
    list.push(slot)
    groups.set(dayKey, list)
  }

  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, daySlots]) => ({
      dayKey,
      label: new Intl.DateTimeFormat(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
      }).format(new Date(daySlots[0].start_time)),
      slots: daySlots.sort((a, b) =>
        a.start_time.localeCompare(b.start_time),
      ),
    }))
}

export function getUserTimezone() {
  return getTimezone()
}
